/**
 * Price Alerts Component
 *
 * Allows users to set price alerts with audio notifications
 * Triggers when real-time price crosses alert thresholds
 */

import React, { useState, useEffect, useRef } from 'react';
import { Bell, X, Plus, TrendingUp, TrendingDown, Volume2 } from 'lucide-react';
import { useRealtimePrice } from '../hooks/useRealtimePrice';

interface PriceAlert {
  id: string;
  symbol: string;
  price: number;
  direction: 'above' | 'below';
  triggered: boolean;
  createdAt: number;
}

interface PriceAlertsProps {
  symbol: string;
  currentPrice?: number;
  className?: string;
}

const PriceAlerts: React.FC<PriceAlertsProps> = ({ symbol, currentPrice, className = '' }) => {
  const [alerts, setAlerts] = useState<PriceAlert[]>([]);
  const [showAddAlert, setShowAddAlert] = useState(false);
  const [newAlertPrice, setNewAlertPrice] = useState('');
  const [newAlertDirection, setNewAlertDirection] = useState<'above' | 'below'>('above');
  const [soundEnabled, setSoundEnabled] = useState(true);

  const livePrice = useRealtimePrice(symbol);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const previousPriceRef = useRef<number | undefined>(undefined);

  // Initialize audio
  useEffect(() => {
    // Create audio context for alert sound (simple beep)
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
    if (AudioContext) {
      const ctx = new AudioContext();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      // Store reference for later use
      audioRef.current = {
        play: () => {
          if (!soundEnabled) return;

          const playCtx = new AudioContext();
          const playOsc = playCtx.createOscillator();
          const playGain = playCtx.createGain();

          playOsc.connect(playGain);
          playGain.connect(playCtx.destination);

          playOsc.frequency.value = 800; // 800 Hz tone
          playGain.gain.setValueAtTime(0.3, playCtx.currentTime);
          playGain.gain.exponentialRampToValueAtTime(0.01, playCtx.currentTime + 0.5);

          playOsc.start(playCtx.currentTime);
          playOsc.stop(playCtx.currentTime + 0.5);
        }
      } as any;
    }
  }, [soundEnabled]);

  // Check alerts when price changes
  useEffect(() => {
    if (!livePrice.price || !livePrice.isLive) return;

    const price = livePrice.price;
    const previousPrice = previousPriceRef.current;
    previousPriceRef.current = price;

    // Check each alert
    alerts.forEach(alert => {
      if (alert.triggered) return; // Already triggered

      const shouldTrigger =
        (alert.direction === 'above' && price >= alert.price && (previousPrice === undefined || previousPrice < alert.price)) ||
        (alert.direction === 'below' && price <= alert.price && (previousPrice === undefined || previousPrice > alert.price));

      if (shouldTrigger) {
        // Trigger alert
        setAlerts(prev =>
          prev.map(a =>
            a.id === alert.id ? { ...a, triggered: true } : a
          )
        );

        // Play sound
        if (audioRef.current && soundEnabled) {
          audioRef.current.play();
        }

        // Show browser notification if permitted
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(`${symbol} Price Alert`, {
            body: `Price crossed ${alert.direction} $${alert.price.toFixed(2)}. Current: $${price.toFixed(2)}`,
            icon: '/favicon.ico',
          });
        }
      }
    });
  }, [livePrice.price, livePrice.isLive, alerts, symbol, soundEnabled]);

  // Request notification permission
  const requestNotificationPermission = () => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  };

  const addAlert = () => {
    const price = parseFloat(newAlertPrice);
    if (isNaN(price) || price <= 0) return;

    const newAlert: PriceAlert = {
      id: Date.now().toString(),
      symbol,
      price,
      direction: newAlertDirection,
      triggered: false,
      createdAt: Date.now(),
    };

    setAlerts(prev => [...prev, newAlert]);
    setNewAlertPrice('');
    setShowAddAlert(false);
    requestNotificationPermission();
  };

  const removeAlert = (id: string) => {
    setAlerts(prev => prev.filter(a => a.id !== id));
  };

  const activeAlerts = alerts.filter(a => !a.triggered);
  const triggeredAlerts = alerts.filter(a => a.triggered);

  return (
    <div className={`bg-slate-800 rounded-lg border border-slate-700 p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Bell size={18} className="text-yellow-400" />
          <h3 className="text-sm font-semibold text-slate-200">Price Alerts</h3>
          {activeAlerts.length > 0 && (
            <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded-full">
              {activeAlerts.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={`p-1.5 rounded transition-colors ${
              soundEnabled
                ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30'
                : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
            }`}
            title={soundEnabled ? 'Sound enabled' : 'Sound disabled'}
          >
            <Volume2 size={14} />
          </button>
          <button
            onClick={() => setShowAddAlert(!showAddAlert)}
            className="p-1.5 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded transition-colors"
            title="Add alert"
          >
            <Plus size={14} />
          </button>
        </div>
      </div>

      {/* Add Alert Form */}
      {showAddAlert && (
        <div className="mb-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700">
          <div className="flex gap-2 mb-2">
            <input
              type="number"
              step="0.01"
              placeholder="Price..."
              value={newAlertPrice}
              onChange={(e) => setNewAlertPrice(e.target.value)}
              className="flex-1 px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-sm text-slate-200 focus:outline-none focus:border-blue-500"
              onKeyPress={(e) => e.key === 'Enter' && addAlert()}
            />
            <select
              value={newAlertDirection}
              onChange={(e) => setNewAlertDirection(e.target.value as 'above' | 'below')}
              className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded text-sm text-slate-200 focus:outline-none focus:border-blue-500"
            >
              <option value="above">Above</option>
              <option value="below">Below</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={addAlert}
              className="flex-1 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded transition-colors"
            >
              Add Alert
            </button>
            <button
              onClick={() => setShowAddAlert(false)}
              className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs font-medium rounded transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Active Alerts */}
      {activeAlerts.length > 0 && (
        <div className="space-y-2 mb-3">
          {activeAlerts.map(alert => (
            <div
              key={alert.id}
              className="flex items-center justify-between p-2 bg-slate-900/50 rounded border border-slate-700/50"
            >
              <div className="flex items-center gap-2">
                {alert.direction === 'above' ? (
                  <TrendingUp size={14} className="text-emerald-400" />
                ) : (
                  <TrendingDown size={14} className="text-red-400" />
                )}
                <span className="text-sm text-slate-300">
                  {alert.direction === 'above' ? 'Above' : 'Below'} <span className="font-mono font-semibold text-slate-100">${alert.price.toFixed(2)}</span>
                </span>
              </div>
              <button
                onClick={() => removeAlert(alert.id)}
                className="p-1 hover:bg-slate-700 rounded transition-colors"
                title="Remove alert"
              >
                <X size={14} className="text-slate-400" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Triggered Alerts */}
      {triggeredAlerts.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs text-slate-500 font-medium">Recently Triggered</div>
          {triggeredAlerts.slice(-3).reverse().map(alert => (
            <div
              key={alert.id}
              className="flex items-center justify-between p-2 bg-yellow-500/10 rounded border border-yellow-500/30"
            >
              <div className="flex items-center gap-2">
                <Bell size={12} className="text-yellow-400" />
                <span className="text-xs text-yellow-300">
                  {alert.direction === 'above' ? 'Above' : 'Below'} ${alert.price.toFixed(2)}
                </span>
              </div>
              <button
                onClick={() => removeAlert(alert.id)}
                className="p-1 hover:bg-yellow-500/20 rounded transition-colors"
                title="Dismiss"
              >
                <X size={12} className="text-yellow-400" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {alerts.length === 0 && !showAddAlert && (
        <div className="text-center py-6 text-slate-500 text-sm">
          <Bell size={32} className="mx-auto mb-2 opacity-50" />
          <p>No price alerts set</p>
          <p className="text-xs mt-1">Click + to add an alert</p>
        </div>
      )}
    </div>
  );
};

export default PriceAlerts;
